import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService as BCUserService } from '@vendasta/business-center';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { Breadcrumbs } from '@vendasta/uikit';
import { merge, Observable, of, Subscription } from 'rxjs';
import { filter, map, scan, switchMap, take } from 'rxjs/operators';
import { HeaderButton, NavigationDepth } from '../../common/header-button';
import { VaDialogService } from '../../common/va-dialog.service';
import { PartnerService } from '../../core/partner.service';
import { User } from '../../core/user';
import { ResendWelcomeEmailDialogComponent } from '../resend-welcome-email-dialog/resend-welcome-email-dialog.component';
import { UserService } from '../user.service';

@Component({
  selector: 'app-user-profile-page',
  templateUrl: './user-profile-page.component.html',
  styleUrls: ['./user-profile-page.component.scss'],
  standalone: false,
})
export class UserProfilePageComponent implements OnInit, OnDestroy {
  pageTitle$: Observable<string>;
  breadcrumbs$: Observable<Breadcrumbs[]>;
  user$: Observable<User>;
  headerButtons$: Observable<HeaderButton[]>;
  subscriptions: Subscription[] = [];
  impersonationUrl$: Observable<string>;

  constructor(
    private route: ActivatedRoute,
    public userService: UserService,
    private dialogService: VaDialogService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private bcUserService: BCUserService,
    private partnerService: PartnerService,
    private dialog: MatDialog,
    private ssoService: SSOService,
  ) {}

  ngOnInit(): void {
    const userId$ = this.route.params.pipe(map((params) => params.userId));

    this.user$ = userId$.pipe(
      switchMap((userId) =>
        this.userService.getUser$(userId, ['SUBJECT_ID', 'USER_ID', 'EMAIL', 'ATTRIBUTES', 'STRUCT_ATTRIBUTES']),
      ),
    );

    this.headerButtons$ = this.user$.pipe(
      map((user) => [
        {
          title: 'MANAGE_ACCOUNTS.USERS_BOX.EDIT_USER',
          icon: 'edit',
          url: `/bc-admin/user/${user.userId}/edit/`,
          navigationDepth: NavigationDepth.tertiary,
        },
        {
          title: 'MANAGE_ACCOUNTS.USERS_BOX.EDIT_PERMISSIONS',
          icon: 'vpn_key',
          url: `/bc-admin/users/${user.userId}/permissions/`,
          navigationDepth: NavigationDepth.tertiary,
        },
        {
          title: 'MANAGE_ACCOUNTS.USERS_BOX.EDIT_NOTIFICATIONS',
          icon: 'notifications',
          url: `/bc-admin/users/${user.userId}/notifications/`,
          navigationDepth: NavigationDepth.tertiary,
        },
        {
          title: 'MANAGE_ACCOUNTS.USERS_BOX.RESEND_WELCOME_EMAIL',
          icon: 'email',
          callback: () => this.presentSendWelcomeEmailDialog(user),
          navigationDepth: NavigationDepth.tertiary,
        },
        {
          title: 'USERS.DELETE_DIALOG.DELETE_USER',
          showTopDivider: true,
          icon: 'close',
          callback: () => this.deleteUser(user),
          navigationDepth: NavigationDepth.tertiary,
        },
      ]),
    );

    this.pageTitle$ = this.user$.pipe(map((user) => user.fullName || user.email));

    this.impersonationUrl$ = this.user$.pipe(
      switchMap((user) =>
        this.ssoService.getEntryUrl('VBC', newPartnerServiceContext(this.partnerService.partnerId), {
          impersonation: user.unifiedUserId,
        }),
      ),
    );

    this.breadcrumbs$ = merge(
      of({ url: '/bc-admin', text: 'COMMON.USERS' }),
      this.pageTitle$.pipe(map((pageTitle) => ({ text: pageTitle }))),
    ).pipe(scan((a, c) => [...a, c], []));
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  presentSendWelcomeEmailDialog(user: User): void {
    this.partnerService
      .getBusinessCenterHostname()
      .pipe(take(1))
      .subscribe((hostName) =>
        this.dialog.open(ResendWelcomeEmailDialogComponent, {
          data: {
            userID: user.unifiedUserId,
            partnerID: this.partnerService.partnerId,
            loginUrl: hostName + '/login/',
          },
          width: '600px',
        }),
      );
  }

  deleteUser(user: User): void {
    this.subscriptions.push(
      this.dialogService
        .delete(
          'COMMON.ACTION_LABELS.DELETE',
          this.translateService.instant('USERS.DELETE_DIALOG.ARE_YOU_SURE', { name: user.email }),
          'COMMON.ACTION_LABELS.DELETE',
        )
        .pipe(
          filter((result) => result === 'yes'),
          take(1),
        )
        .subscribe(() => {
          this.userService.delete(user);
        }),
    );
  }
}
